import { useEffect, useRef } from 'react'

/**
 * Non-overlapping, visibility-aware polling — a safe replacement for Apollo's
 * `pollInterval`, which fires a fresh request every tick even if the last is
 * still in flight (so a slow response stacks and saturates the pool — the
 * poker polling death-spiral). This waits for each refetch to settle before
 * scheduling the next, and skips while the tab is hidden.
 *
 * Local to this app on purpose: a shared-package hook importing `react` fails
 * the per-app Docker build (shared is `link:`ed without its own node_modules).
 */
export function useLivePoll(
  refetch: () => Promise<unknown>,
  intervalMs: number,
  active = true
): void {
  const refetchRef = useRef(refetch)
  refetchRef.current = refetch

  useEffect(() => {
    if (!active) return undefined
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined

    const run = async () => {
      if (typeof document === 'undefined' || !document.hidden) {
        try {
          await refetchRef.current()
        } catch {
          /* transient — the next tick retries */
        }
      }
      if (!cancelled) timer = setTimeout(run, intervalMs)
    }

    timer = setTimeout(run, intervalMs)
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [intervalMs, active])
}
